import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  UserGroupIcon,
  HomeIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  CubeIcon,
  UserGroupIcon as CustomersIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useCompany } from './CompanyContext';
import { useUser } from './UserContext';
import { useAuth } from './AuthContext';
import CompanySelector from './CompanySelector';
import SubscriptionBanner from './SubscriptionBanner';
import { useTranslation } from 'react-i18next';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { selectedCompany, companies, loading: companiesLoading } = useCompany();
  const { isAdmin } = useUser();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const navigation = [
    { name: t('nav.dashboard'), href: '/', icon: HomeIcon },
    ...(isAdmin ? [{ name: t('nav.companies'), href: '/companies', icon: BuildingOfficeIcon }] : []),
    { name: t('nav.users'), href: '/users', icon: UsersIcon },
    { name: t('nav.employees'), href: '/employees', icon: UserGroupIcon },
    { name: t('nav.customers'), href: '/customers', icon: CustomersIcon },
    { name: t('nav.locations'), href: '/locations', icon: MapPinIcon },
    { name: t('nav.orders'), href: '/orders', icon: ShoppingCartIcon },
    { name: t('nav.loans'), href: '/loans', icon: CreditCardIcon },
    { name: t('nav.sales'), href: '/sales', icon: CurrencyDollarIcon },
    { name: t('nav.reporting'), href: '/reporting', icon: ChartBarIcon },
    { name: t('nav.products'), href: '/products', icon: CubeIcon },
    { name: t('nav.settings'), href: '/settings', icon: Cog6ToothIcon },
    ...(isAdmin ? [
      { name: t('nav.subscriptions'), href: '/subscriptions', icon: CreditCardIcon },
      { name: t('nav.currencies'), href: '/currencies', icon: CurrencyDollarIcon },
    ] : []),
  ];

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-large border-r border-gray-200 flex flex-col">
        <div className="flex h-16 items-center justify-center border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="flex items-center space-x-3">
            <div className="w-24 h-10 bg-white rounded-lg flex items-center justify-center p-1">
              <img 
                src="/regixor_logo.png" 
                alt="Regixor Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
        
        {/* Company Selector - Show for all users when companies are available */}
        {(() => {
          const companiesArray = Array.isArray(companies) ? companies : [];
          
          // Show company selector if companies are available or still loading
          if (companiesLoading || companiesArray.length > 0) {
            return (
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('company.currentCompany')}
                  </label>
                  <CompanySelector />
                </div>
                {selectedCompany && isAdmin && (
                  <div className="text-xs text-gray-600 mt-2">
                    <p className="font-medium">{selectedCompany.name}</p>
                    {selectedCompany.city && selectedCompany.state && (
                      <p>{selectedCompany.city}, {selectedCompany.state}</p>
                    )}
                  </div>
                )}
              </div>
            );
          }
          return null;
        })()}
        
        <nav className="mt-4 px-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            
            {navigation.map((item) => {
              // Special handling for dashboard route - only exact match
              const isActive = item.href === '/' 
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-soft'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-soft'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 transition-colors duration-200 ${
                    isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'
                  }`} />
                  {item.name}
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-primary-600 rounded-full animate-bounce-gentle"></div>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="mt-auto p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <UsersIcon className="w-4 h-4 text-primary-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-gray-900">
                  {isAdmin ? t('user.systemAdmin') : t('user.user')}
                </p>
                <p className="text-gray-500">
                  {isAdmin ? t('user.administrator') : selectedCompany?.name || t('user.noCompany')}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200 hover:shadow-soft"
              title={t('actions.logout')}
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="pl-64">
        <main className="py-8 px-8 animate-fade-in">
          <div className="max-w-7xl mx-auto">
            <SubscriptionBanner />
            {children}
          </div>
        </main>
      </div>

      {/* Mobile menu button (hidden on desktop) */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button className="p-2 bg-white rounded-lg shadow-medium border border-gray-200">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Layout;
